import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';

import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

import { LanguageService, ThemeService } from '@ose/services';
import { ThemeMode } from '@ose/types';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    LoadingBarRouterModule,
    TranslateModule,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent {
  title = 'OneSecond Client';

  currentLang = 'fa';
  currentTheme: ThemeMode = 'light';

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);

  constructor() {
    this.listenToRouterEvents();
    this.listenToLanguageChanges();
    this.listenToThemeChanges();
  }

  private listenToRouterEvents(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart | NavigationError =>
            event instanceof NavigationStart || event instanceof NavigationError
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.languageService.syncLanguageFromUrl(event.url);
          return;
        }

        const errorMessage = event.error?.message as string | undefined;

        if (
          errorMessage?.includes('Failed to fetch dynamically imported module')
        ) {
          window.location.replace(event.url);
        }
      });
  }

  private listenToLanguageChanges(): void {
    this.languageService
      .getCurrentLanguage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((language: string) => {
        this.currentLang = language;

        document.documentElement.lang = language;
        document.documentElement.dir =
          language === 'fa' || language === 'ar' ? 'rtl' : 'ltr';
      });
  }

  private listenToThemeChanges(): void {
    this.themeService
      .getThemeMode()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((theme: ThemeMode) => {
        this.currentTheme = theme;

        document.documentElement.setAttribute('data-bs-theme', theme);
      });
  }
}
